<template>
  <div class="page-container help-page">
    <portal to="page-name">Central de Tutoriais em Vídeo</portal>
    <div class="card card-page">
      <div class="card-body">
        <div class="help-intro">
          <div class="help-intro-text">
            <h1 class="help-title">Central de Tutoriais em Vídeo</h1>
            <p class="help-lead">
              Bem-vindo! Aqui você encontra vídeos curtos que mostram, passo a passo,
              como usar os recursos do sistema.
            </p>
            <p class="help-lead hide-sm">
              Cada vídeo foi preparado para explicar um recurso do início ao fim.
              Reserve alguns minutos para explorar e tirar o máximo do Stenci.
            </p>
            <div class="help-support">
              <span class="help-support-icon">
                <fa-icon :icon="['fal', 'headset']"/>
              </span>
              <div>
                <strong>Ficou com dúvidas?</strong>
                Fale com a nossa equipe de suporte pelo WhatsApp
                <a :href="supportLink" target="_blank" rel="noopener" class="help-support-link">
                  <fa-icon :icon="['fab', 'whatsapp']"/>{{ supportPhone }}
                </a>.
                Estamos aqui para ajudar você a aproveitar ao máximo o sistema.
              </div>
            </div>
          </div>
          <div class="help-intro-image hide-sm">
            <img src="../../../assets/images/login-elements.png" alt="Stenci">
          </div>
        </div>

        <div class="help-startup">
          <label class="form-switch help-startup-toggle">
            <input type="checkbox" v-model="hideOnStartup" @change="saveHideOnStartup">
            <i class="form-icon"/>Não mostrar esta tela na inicialização do sistema
          </label>
          <span class="help-startup-hint">
            Para voltar, use o menu do seu usuário, em Tutoriais.
          </span>
        </div>

        <a
          :href="featured.url"
          target="_blank"
          rel="noopener"
          class="help-featured"
        >
          <div class="help-featured-thumb">
            <img :src="cover" :alt="featured.title">
          </div>
          <div class="help-featured-body">
            <span class="help-featured-badge">Comece por aqui</span>
            <div class="help-featured-title">{{ featured.title }}</div>
            <div class="help-featured-description">{{ featured.description }}</div>
            <span class="btn btn-primary btn-icon btn-icon-left help-featured-action">
              <fa-icon :icon="['fab', 'youtube']"/>Assistir agora
            </span>
          </div>
        </a>

        <div class="help-section-head">
          <span class="help-section-title">Vídeos disponíveis</span>
          <span class="help-section-count">{{ videos.length }}</span>
        </div>

        <div class="help-videos">
          <a v-for="(item, i) in videos" :key="i"
             :href="item.url" target="_blank" rel="noopener"
             class="help-video">
            <div class="help-video-thumb">
              <img :src="item.cover || cover" :alt="item.title">
            </div>
            <div class="help-video-body">
              <div class="help-video-title">{{ item.title }}</div>
              <div class="help-video-description">{{ item.description }}</div>
            </div>
            <div class="help-video-action">
              <fa-icon :icon="['fab', 'youtube']"/>
              <span class="hide-sm">Assistir</span>
              <fa-icon :icon="['fal', 'external-link']" class="help-video-action-icon"/>
            </div>
          </a>
        </div>

        <div class="help-section-head">
          <span class="help-section-title">Acesso remoto para o suporte</span>
          <span class="help-section-count">
            {{ remoteAccess.name }} {{ remoteAccess.version }}
          </span>
        </div>

        <p class="help-lead help-remote-intro">
          Quando precisar de ajuda, nossa equipe de suporte pode ver e controlar o seu
          computador, com a sua autorização, usando o {{ remoteAccess.name }}, um programa
          gratuito de acesso remoto.
        </p>

        <div v-if="remoteOpenMessage"
             class="help-remote-status" :class="`help-remote-status-${remoteOpenVariant}`">
          {{ remoteOpenMessage }}
        </div>

        <button v-if="!isRemoteMobile" type="button"
                class="btn btn-primary btn-icon btn-icon-left help-remote-open"
                :class="{ loading: isRemoteOpening }" :disabled="isRemoteOpening"
                @click="openRemoteAccess">
          <fa-icon :icon="['fal', 'desktop']"/>{{ remoteAccess.openLabel }}
        </button>

        <ol class="help-remote-steps">
          <li v-for="(step, i) in remoteAccess.steps" :key="i">
            <strong>{{ step.title }}</strong>
            {{ step.text }}
          </li>
        </ol>

        <div v-if="remoteDownloadMessage" class="help-remote-status help-remote-status-success">
          {{ remoteDownloadMessage }}
        </div>

        <div class="help-downloads">
          <div v-for="platform in remoteAccess.platforms" :key="platform.key"
               class="help-download">
            <div class="help-download-icon">
              <fa-icon :icon="['fab', platform.icon]"/>
            </div>
            <div class="help-download-body">
              <div class="help-download-title">
                {{ platform.title }}
                <span v-if="platform.key === remotePlatformKey" class="help-download-badge">
                  Este computador
                </span>
              </div>
              <div class="help-download-description">{{ platform.description }}</div>
            </div>
            <div class="help-download-actions">
              <a v-for="item in platform.downloads" :key="item.url"
                 :href="item.url" rel="noopener"
                 class="btn btn-sm btn-icon btn-icon-left"
                 @click="handleRemoteDownload(platform)">
                <fa-icon :icon="['fal', 'download']"/>{{ item.label }}
              </a>
            </div>
          </div>
        </div>

        <a :href="remoteAccess.releaseUrl" target="_blank" rel="noopener" class="help-remote-more">
          Outras versões (Fedora, Arch, AppImage, 32 bits e celular) na página oficial do
          {{ remoteAccess.name }}
          <fa-icon :icon="['fal', 'external-link']"/>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { whatsappURL } from '@/helpers/link';
import isTutorialHidden from '../tutorial';
import cover from '../../../assets/images/capa-azul.jpg';

const REMOTE_ACCESS_VERSION = '1.5.0';
const REMOTE_ACCESS_DOWNLOAD_URL = `https://github.com/rustdesk/rustdesk/releases/download/${REMOTE_ACCESS_VERSION}`;

const REMOTE_ACCESS_LAUNCH_URL = 'rustdesk://';
const REMOTE_OPEN_TIMEOUT_MS = 2500;
const REMOTE_PLATFORM_MOBILE = 'mobile';

const REMOTE_OPEN_VARIANTS = {
  opening: 'info',
  opened: 'success',
  missing: 'warning',
};

const remoteAccessFileUrl = suffix => (
  `${REMOTE_ACCESS_DOWNLOAD_URL}/rustdesk-${REMOTE_ACCESS_VERSION}-${suffix}`
);

const detectRemotePlatform = () => {
  const { userAgent, maxTouchPoints } = window.navigator;

  if (/android|iphone|ipad|ipod/i.test(userAgent)) return REMOTE_PLATFORM_MOBILE;
  if (/macintosh/i.test(userAgent) && maxTouchPoints > 1) return REMOTE_PLATFORM_MOBILE;
  if (/windows/i.test(userAgent)) return 'windows';
  if (/macintosh|mac os x/i.test(userAgent)) return 'mac';
  if (/linux|x11/i.test(userAgent)) return 'linux';

  return '';
};

export default {
  data() {
    return {
      cover,
      hideOnStartup: false,
      supportPhone: '(41) 9 9995-0939',
      featured: {
        title: 'Como configurar o sistema do zero',
        description: `Aprenda a configurar o Stenci do zero em poucos minutos, desde o primeiro
         acesso até a criação da agenda e o agendamento do primeiro paciente.`,
        url: 'https://youtu.be/tDx7hWg2waE',
      },
      videos: [
        {
          title: 'Login, senha e primeiro acesso',
          description: `Aprenda a entrar no Stenci com seus dados de acesso, recuperar a senha,
           trocar de unidade e alternar entre os módulos do sistema`,
          url: 'https://youtu.be/Twa6t6cDCz4',
        },
        {
          title: 'Como criar e gerenciar agendas',
          description: `Aprenda a gerenciar as agendas do Stenci, desde a criação e configuração
           dos horários até a edição, exclusão e atualização de profissionais, convênios e
            serviços.`,
          url: 'https://youtu.be/hhop2XCIWLo',
        },
        {
          title: 'Como usar os Agentes de IA no Prontuário',
          description: `Aprenda a utilizar os recursos de Inteligência Artificial do prontuário,
           desde o resumo do paciente e a transcrição do atendimento até a sugestão de CID e
            a verificação de interações medicamentosas.`,
          url: 'https://youtu.be/vLhZ0XbFgXM',
        },
        // {
        //   title: 'Visão geral',
        //   description: `Tenha uma visão geral dos principais recursos do sistema
        //    em um único vídeo explicativo`,
        //   url: '',
        // },
        // {
        //   title: 'Agenda',
        //   description: `Explore como organizar compromissos e horários com eficiência,
        //   seja para consultas médicas ou exames`,
        //   url: 'https://www.youtube.com/watch?v=u0PqDmtcP_Y',
        // },
        // {
        //   title: 'Prontuário eletrônico',
        //   description: `Aprenda a utilizar o prontuário eletrônico do sistema,
        //    conheça os recursos e como acessar às informações dos pacientes`,
        //   url: 'https://www.youtube.com/watch?v=_Wc-ywXCwUQ',
        // },
        // {
        //   title: 'Gerenciamento da empresa',
        //   description: `Aprenda configurar sua empresa, configurando elementos como logomarca,
        //    margens e outras preferências`,
        //   url: 'https://www.youtube.com/watch?v=y-MAMJAbbos',
        // },
        // {
        //   title: 'Cadastrar colaboradores',
        //   description: `Saiba como cadastrar e gerenciar informações dos colaboradores
        //   e permissões de acesso`,
        //   url: 'https://www.youtube.com/watch?v=7-L19Zyr9SY',
        // },
        // {
        //   title: 'Cadastrar serviços, taxas e outras despesas',
        //   description: 'Explore como adicionar e gerir serviços no sistema',
        //   url: 'https://www.youtube.com/watch?v=phQbWwFlT9M',
        // },
        // {
        //   title: 'Cadastrar convênios',
        //   description: 'Saiba como cadastrar e gerenciar convênios médicos no sistema',
        //   url: 'https://www.youtube.com/watch?v=vVvWv4vAEKE',
        // },
        // {
        //   title: 'Cadastrar profissionais do corpo clínico',
        //   description: 'Aprenda como cadastrar e gerenciar informações do corpo clínico',
        //   url: 'https://www.youtube.com/watch?v=vxNPHcV33nQ',
        // },
        // {
        //   title: 'Configuração da agenda',
        //   description: `Descubra como personalizar e configurar as agendas no sistema,
        //    permitindo uma gestão eficiente dos horários de atendimento`,
        //   url: 'https://www.youtube.com/watch?v=hmJ3wJMA2y0',
        // },
        // {
        //   title: 'Gerenciar modelos de impressos e solicitações',
        //   description: `Descubra como personalizar e configurar solicitações e impressos,
        //    permitindo um atendimento eficiente aos pacientes`,
        //   url: 'https://www.youtube.com/watch?v=Izz-PMsGmzI',
        // },
        {
          title: 'Como configurar a emissão de Nota Fiscal',
          description: `Aprenda a configurar a emissão de Nota Fiscal de Serviço (NFS-e) no Stenci,
           desde a ativação da integração até o preenchimento das configurações fiscais e
            dos dados de acesso.`,
          url: 'https://youtu.be/3IWjjdjJNM8',
        },
        {
          title: 'Como usar o Novo Caixa',
          description: `Aprenda a utilizar o Novo Caixa do Stenci, desde a configuração inicial
           até o fechamento e a conferência da sessão.`,
          url: 'https://youtu.be/fO2tTZGWgGg',
        },
      ],
      remoteOpenStatus: '',
      remotePlatformKey: '',
      remoteLeftPage: false,
      remoteDownloaded: null,
      remoteAccess: {
        name: 'RustDesk',
        version: REMOTE_ACCESS_VERSION,
        openLabel: 'Abrir o RustDesk',
        releaseUrl: `https://github.com/rustdesk/rustdesk/releases/tag/${REMOTE_ACCESS_VERSION}`,
        steps: [
          {
            title: 'Abra o RustDesk.',
            text: `Clique em “Abrir o RustDesk”. Se ele já estiver instalado, a janela abre na hora;
             se não abrir, baixe e instale pelo quadro abaixo e clique de novo.`,
          },
          {
            title: 'Passe o ID e a senha ao atendente.',
            text: `Na tela inicial do RustDesk ficam o ID e a senha de acesso (clique no ícone de
             olho para ver a senha). Informe os dois ao atendente pelo WhatsApp.`,
          },
          {
            title: 'Acompanhe o acesso.',
            text: `Com a senha, a conexão entra direto e uma janela do RustDesk mostra quem está
             conectado. Você acompanha tudo pela tela e pode encerrar o acesso a qualquer momento.`,
          },
        ],
        platforms: [
          {
            key: 'windows',
            icon: 'windows',
            title: 'Windows',
            description: 'Windows 10 ou mais recente, 64 bits.',
            installHint: 'abra o arquivo baixado e clique em Instalar',
            downloads: [
              { label: 'Baixar para Windows', url: remoteAccessFileUrl('x86_64.exe') },
            ],
          },
          {
            key: 'mac',
            icon: 'apple',
            title: 'Mac',
            description: 'Escolha pelo processador do seu Mac: menu Apple › Sobre este Mac.',
            installHint: `abra o arquivo .dmg, arraste o RustDesk para a pasta Aplicativos e, na
             primeira abertura, autorize as permissões que o macOS pedir`,
            downloads: [
              { label: 'Chip Apple (M1 ou mais novo)', url: remoteAccessFileUrl('aarch64.dmg') },
              { label: 'Chip Intel', url: remoteAccessFileUrl('x86_64.dmg') },
            ],
          },
          {
            key: 'linux',
            icon: 'linux',
            title: 'Linux',
            description: 'Ubuntu, Debian e Linux Mint (pacote .deb, 64 bits).',
            installHint: 'abra o arquivo .deb para instalar pela central de programas do sistema',
            downloads: [
              { label: 'Baixar para Linux', url: remoteAccessFileUrl('x86_64.deb') },
            ],
          },
        ],
      },
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    supportLink() {
      return whatsappURL(this.supportPhone, 'Olá! Preciso de ajuda com o Stenci.');
    },
    isRemoteOpening() {
      return this.remoteOpenStatus === 'opening';
    },
    isRemoteMobile() {
      return this.remotePlatformKey === REMOTE_PLATFORM_MOBILE;
    },
    remoteOpenVariant() {
      return REMOTE_OPEN_VARIANTS[this.remoteOpenStatus] || 'info';
    },
    remoteOpenMessage() {
      const { name, openLabel } = this.remoteAccess;
      if (this.isRemoteMobile) {
        return `Abra esta tela no computador que vai receber o acesso: pelo celular não dá para
         abrir nem instalar o ${name} daqui.`;
      }
      switch (this.remoteOpenStatus) {
        case 'opening':
          return `Abrindo o ${name}… Se o navegador perguntar, confirme a abertura.`;
        case 'opened':
          return `O ${name} deve estar aberto: informe ao atendente o ID e a senha da tela
           inicial. Se a janela não apareceu, baixe e instale pelo quadro abaixo.`;
        case 'missing':
          return `O ${name} não abriu, então ainda não está instalado neste computador. Baixe e
           instale pelo quadro abaixo e depois clique em “${openLabel}” de novo.`;
        default:
          return '';
      }
    },
    remoteDownloadMessage() {
      if (!this.remoteDownloaded) return '';
      return `Download iniciado. Quando terminar, ${this.remoteDownloaded.installHint}. Depois,
       volte aqui e clique em “${this.remoteAccess.openLabel}”.`;
    },
  },
  mounted() {
    this.hideOnStartup = isTutorialHidden(this.user);
    this.remotePlatformKey = detectRemotePlatform();
    window.addEventListener('blur', this.handleRemotePageLeave);
    document.addEventListener('visibilitychange', this.handleRemotePageLeave);
  },
  beforeDestroy() {
    this.clearRemoteOpenWatch();
    window.removeEventListener('blur', this.handleRemotePageLeave);
    document.removeEventListener('visibilitychange', this.handleRemotePageLeave);
  },
  methods: {
    ...mapActions(['setUser']),
    openRemoteAccess() {
      if (this.isRemoteOpening) return;

      this.clearRemoteOpenWatch();
      this.remoteLeftPage = false;
      this.remoteOpenStatus = 'opening';

      this.remoteLaunchFrame = document.createElement('iframe');
      this.remoteLaunchFrame.hidden = true;
      this.remoteLaunchFrame.src = REMOTE_ACCESS_LAUNCH_URL;
      document.body.appendChild(this.remoteLaunchFrame);

      this.remoteOpenTimer = window.setTimeout(() => {
        this.remoteOpenStatus = this.remoteLeftPage ? 'opened' : 'missing';
        this.clearRemoteOpenWatch();
      }, REMOTE_OPEN_TIMEOUT_MS);
    },
    clearRemoteOpenWatch() {
      window.clearTimeout(this.remoteOpenTimer);
      if (this.remoteLaunchFrame) {
        this.remoteLaunchFrame.remove();
        this.remoteLaunchFrame = null;
      }
    },
    handleRemotePageLeave() {
      if (!this.isRemoteOpening) return;
      if (document.hidden || !document.hasFocus()) this.remoteLeftPage = true;
    },
    handleRemoteDownload(platform) {
      this.remoteDownloaded = platform;
    },
    saveHideOnStartup() {
      const { hideOnStartup } = this;
      this.$httpX.put('/me/preferences', { tutorial: { hideOnStartup } })
        .then(({ data }) => {
          this.setUser({ ...this.user, preferences: data });
          this.$toast.show(hideOnStartup
            ? 'Esta tela não será mais aberta ao entrar no sistema'
            : 'Esta tela voltará a abrir ao entrar no sistema');
        })
        .catch(() => {
          this.hideOnStartup = !hideOnStartup;
        });
    },
  },
};
</script>

<style lang="scss">
  @import "./src/assets/scss/variables";
  .help-page {
    .card-page > .card-body {
      padding: $layout-spacing-xl;
    }
    .help-intro {
      align-items: center;
      display: grid;
      gap: $layout-spacing-xl;
      grid-template-columns: minmax(0, 1fr) auto;
      padding-bottom: $layout-spacing-xl;
      border-bottom: $border-width solid $border-color;
    }
    .help-intro-text {
      max-width: 42rem;
    }
    .help-title {
      color: $secondary-color;
      font-size: 1.1rem;
      font-weight: 600;
      line-height: 1.25;
      margin: 0 0 $layout-spacing-lg;
    }
    .help-lead {
      color: $gray-color-dark;
      font-size: $font-size;
      line-height: 1.6;
      margin: 0 0 $layout-spacing;
    }
    .help-support {
      align-items: center;
      background-color: lighten($info-color, 48%);
      border: $border-width solid lighten($info-color, 34%);
      border-left: 3px solid $info-color;
      border-radius: $border-radius;
      color: $secondary-color-dark;
      display: flex;
      font-size: $font-size-sm;
      gap: $layout-spacing;
      line-height: 1.5;
      margin-top: $layout-spacing-lg;
      padding: $layout-spacing $layout-spacing-lg;
      strong {
        display: block;
        font-weight: 600;
      }
    }
    .help-support-link {
      color: $secondary-color-dark;
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 2px;
      white-space: nowrap;
      svg {
        color: #25D366;
        margin-right: $layout-spacing-sm;
      }
      &:hover {
        color: $primary-color-dark;
      }
    }
    .help-support-icon {
      align-items: center;
      background-color: $light-color;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0, 0, 0, .08);
      color: $info-color;
      display: flex;
      flex-shrink: 0;
      font-size: .9rem;
      height: 1.8rem;
      justify-content: center;
      width: 1.8rem;
    }
    .help-intro-image {
      img {
        display: block;
        max-width: 11rem;
      }
    }
    .help-featured {
      align-items: center;
      background-color: #FFF8E6;
      border: $border-width solid #F2C94C;
      border-left: 4px solid #F2C94C;
      border-radius: $border-radius * 2;
      color: $body-font-color;
      display: grid;
      gap: $layout-spacing-xl;
      grid-template-columns: auto minmax(0, 1fr);
      margin-top: $layout-spacing-xl;
      padding: $layout-spacing-lg $layout-spacing-xl;
      text-decoration: none;
      transition: box-shadow .15s ease;
      &:hover, &:focus {
        box-shadow: 0 4px 14px rgba(#8A5A00, .15);
        text-decoration: none;
      }
    }
    .help-featured-thumb {
      border: $border-width solid #F2C94C;
      border-radius: $border-radius;
      height: 6rem;
      overflow: hidden;
      position: relative;
      width: 10.6rem;
      img {
        display: block;
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
    }
    .help-featured-body {
      min-width: 0;
    }
    .help-featured-badge {
      background-color: #8A5A00;
      border-radius: 1rem;
      color: $light-color;
      display: inline-block;
      font-size: .5rem;
      font-weight: 700;
      letter-spacing: .06em;
      line-height: 1;
      margin-bottom: $layout-spacing;
      padding: .22rem .5rem;
      text-transform: uppercase;
    }
    .help-featured-title {
      color: $gray-color-900;
      font-size: $font-size-lg;
      font-weight: 700;
      line-height: 1.3;
      margin-bottom: $layout-spacing-sm;
    }
    .help-featured-description {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      line-height: 1.5;
      margin-bottom: $layout-spacing-lg;
      max-width: 44rem;
    }
    .help-featured-action {
      pointer-events: none;
    }
    .help-section-head {
      align-items: center;
      display: flex;
      gap: $layout-spacing;
      margin: $layout-spacing-xl 0 $layout-spacing-lg;
    }
    .help-section-title {
      color: $secondary-color;
      font-size: $font-size-lg;
      font-weight: 600;
    }
    .help-section-count {
      background-color: $gray-color-light;
      border-radius: 1rem;
      color: $gray-color-dark;
      font-size: $font-size-xs;
      font-weight: 600;
      line-height: 1;
      padding: $layout-spacing-sm $layout-spacing;
    }
    .help-videos {
      border: $border-width solid $border-color;
      border-radius: $border-radius * 2;
      overflow: hidden;
    }
    .help-video {
      align-items: center;
      color: $body-font-color;
      display: grid;
      gap: $layout-spacing-lg;
      grid-template-columns: auto minmax(0, 1fr) auto;
      padding: $layout-spacing $layout-spacing-lg;
      text-decoration: none;
      transition: background-color .15s ease;
      & + .help-video {
        border-top: $border-width solid $border-color;
      }
      &:hover, &:focus {
        background-color: lighten($primary-color, 46%);
        text-decoration: none;
        .help-video-title {
          color: $primary-color-dark;
        }
        .help-video-action {
          color: $primary-color-dark;
        }
      }
    }
    .help-video-thumb {
      background-color: $gray-color-light;
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      flex-shrink: 0;
      height: 3.4rem;
      overflow: hidden;
      position: relative;
      width: 6rem;
      img {
        display: block;
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
    }
    .help-video-body {
      min-width: 0;
    }
    .help-video-title {
      color: $secondary-color;
      font-size: $font-size;
      font-weight: 600;
      line-height: 1.35;
      margin-bottom: $layout-spacing-sm;
      transition: color .15s ease;
    }
    .help-video-description {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      line-height: 1.5;
      max-width: 48rem;
    }
    .help-video-action {
      align-items: center;
      color: $primary-color;
      display: flex;
      font-size: $font-size-sm;
      font-weight: 600;
      gap: $layout-spacing-sm;
      transition: color .15s ease;
      white-space: nowrap;
    }
    .help-video-action-icon {
      font-size: $font-size-xs;
    }
    .help-remote-intro {
      max-width: 48rem;
    }
    .help-remote-status {
      border: $border-width solid;
      border-left-width: 3px;
      border-radius: $border-radius;
      font-size: $font-size-sm;
      line-height: 1.5;
      margin-bottom: $layout-spacing-lg;
      max-width: 48rem;
      padding: $layout-spacing $layout-spacing-lg;
      &.help-remote-status-info {
        background-color: lighten($info-color, 48%);
        border-color: lighten($info-color, 34%);
        border-left-color: $info-color;
        color: $secondary-color-dark;
      }
      &.help-remote-status-success {
        background-color: lighten($success-color, 48%);
        border-color: lighten($success-color, 30%);
        border-left-color: $success-color;
        color: darken($success-color, 20%);
      }
      &.help-remote-status-warning {
        background-color: lighten($warning-color, 40%);
        border-color: lighten($warning-color, 25%);
        border-left-color: $warning-color;
        color: darken($warning-color, 25%);
      }
    }
    .help-remote-open {
      margin-bottom: $layout-spacing-lg;
    }
    .help-download-badge {
      background-color: $primary-color;
      border-radius: 1rem;
      color: $light-color;
      display: inline-block;
      font-size: .5rem;
      font-weight: 700;
      letter-spacing: .06em;
      line-height: 1;
      margin-left: $layout-spacing-sm;
      padding: .22rem .5rem;
      text-transform: uppercase;
      vertical-align: middle;
    }
    .help-remote-steps {
      counter-reset: remote-step;
      display: grid;
      gap: $layout-spacing;
      list-style: none;
      margin: 0 0 $layout-spacing-lg;
      max-width: 48rem;
      padding: 0;
      li {
        color: $gray-color-dark;
        counter-increment: remote-step;
        font-size: $font-size-sm;
        line-height: 1.5;
        padding-left: 1.5rem;
        position: relative;
        &::before {
          align-items: center;
          background-color: $purple-neon-color-100;
          border-radius: 50%;
          color: $primary-color-dark;
          content: counter(remote-step);
          display: flex;
          font-size: $font-size-xs;
          font-weight: 700;
          height: 1.05rem;
          justify-content: center;
          left: 0;
          position: absolute;
          top: 0;
          width: 1.05rem;
        }
        strong {
          color: $gray-color-900;
          font-weight: 600;
        }
      }
    }
    .help-downloads {
      border: $border-width solid $border-color;
      border-radius: $border-radius * 2;
      overflow: hidden;
    }
    .help-download {
      align-items: center;
      display: grid;
      gap: $layout-spacing-lg;
      grid-template-columns: auto minmax(0, 1fr) auto;
      padding: $layout-spacing $layout-spacing-lg;
      & + .help-download {
        border-top: $border-width solid $border-color;
      }
    }
    .help-download-icon {
      align-items: center;
      background-color: $gray-color-ultra-light;
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      color: $secondary-color;
      display: flex;
      flex-shrink: 0;
      font-size: 1.1rem;
      height: 2.4rem;
      justify-content: center;
      width: 2.4rem;
    }
    .help-download-body {
      min-width: 0;
    }
    .help-download-title {
      color: $secondary-color;
      font-size: $font-size;
      font-weight: 600;
      line-height: 1.35;
      margin-bottom: $layout-spacing-sm;
    }
    .help-download-description {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      line-height: 1.5;
    }
    .help-download-actions {
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing-sm;
      justify-content: flex-end;
    }
    .help-remote-more {
      align-items: center;
      color: $gray-color-dark;
      display: inline-flex;
      font-size: $font-size-sm;
      gap: $layout-spacing-sm;
      margin-top: $layout-spacing;
      &:hover {
        color: $primary-color-dark;
      }
    }
    .help-startup {
      align-items: center;
      background-color: $gray-color-ultra-light;
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing-lg;
      margin-top: $layout-spacing-lg;
      padding: $layout-spacing $layout-spacing-lg;
    }
    .help-startup-toggle {
      color: $gray-color-900;
      font-weight: 600;
      margin: 0;
    }
    .help-startup-hint {
      color: $gray-color-dark;
      font-size: $font-size-xs;
    }
    @media screen and (max-width: $size-sm) {
      .card-page > .card-body {
        padding: $layout-spacing-lg;
      }
      .help-intro {
        grid-template-columns: 1fr;
        padding-bottom: $layout-spacing-lg;
      }
      .help-featured {
        gap: $layout-spacing-lg;
        grid-template-columns: 1fr;
        padding: $layout-spacing-lg;
      }
      .help-startup {
        align-items: flex-start;
        flex-direction: column;
      }
      .help-featured-thumb {
        width: 100%;
      }
      .help-video {
        gap: $layout-spacing;
        padding: $layout-spacing;
      }
      .help-video-thumb {
        height: 2.6rem;
        width: 4.6rem;
      }
      .help-download {
        gap: $layout-spacing;
        grid-template-columns: auto minmax(0, 1fr);
        padding: $layout-spacing;
      }
      .help-download-actions {
        grid-column: 1 / -1;
        justify-content: flex-start;
      }
      .help-video-description {
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        display: -webkit-box;
        overflow: hidden;
      }
      .help-video-action-icon {
        display: none;
      }
      .help-support {
        align-items: flex-start;
      }
    }
  }
</style>
